import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  IdentificationCard,
  MagnifyingGlass,
  X,
  Clock,
  CaretRight,
  Warning,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import {
  fetchMembers,
  fetchMember,
  transitionMember,
  TENANCY_LABEL,
  LIFECYCLE_LABEL,
  type MemberDetail,
  type TenancyType,
  type TransitionAction,
} from '@/lib/membersApi';
import { getApiErrorMessage } from '@/lib/api';
import { ScrollLock } from '@/components/shared/ScrollLock';

const TENANCY_OPTIONS: TenancyType[] = ['pupil', 'full_member', 'door_tenant', 'associate', 'staff'];

function LifecycleBadge({ value }: { value: 'active' | 'former' | 'retired' }) {
  const cls =
    value === 'active'
      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300'
      : value === 'retired'
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300'
        : 'bg-muted text-muted-foreground';
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{LIFECYCLE_LABEL[value]}</span>;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function MembersPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const membersQ = useQuery({ queryKey: ['members'], queryFn: fetchMembers });
  const members = membersQ.data ?? [];

  if (membersQ.isError) {
    return <QueryErrorState what="members" />;
  }

  const filtered = useMemo(() => {
    if (!q.trim()) return members;
    const s = q.toLowerCase();
    return members.filter((m) => `${m.firstName} ${m.lastName} ${m.email}`.toLowerCase().includes(s));
  }, [members, q]);

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Members"
        subtitle="Everyone who belongs to the chambers — their tenancy and lifecycle over time."
      />

      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search members…"
            className="h-9 w-full rounded-lg border border-border bg-card pl-9 pr-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {membersQ.isLoading ? (
            <div className="py-16 text-center text-sm text-muted-foreground">Loading members…</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">No members found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-3 sm:px-5">Member</th>
                    <th className="hidden px-5 py-3 md:table-cell">Membership</th>
                    <th className="px-3 py-3 sm:px-5">Status</th>
                    <th className="hidden px-5 py-3 md:table-cell">Since</th>
                    <th className="w-8 px-3 py-3 sm:px-5"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((m) => (
                    <tr
                      key={m.id}
                      onClick={() => setSelected(m.id)}
                      className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-muted/40"
                    >
                      <td className="px-3 py-3 sm:px-5">
                        <div className="font-medium text-foreground">{m.firstName} {m.lastName}</div>
                        <div className="truncate text-xs text-muted-foreground">{m.email}</div>
                        <div className="mt-0.5 text-xs md:hidden">
                          {m.tenure ? (
                            <span className="text-muted-foreground">{TENANCY_LABEL[m.tenure.tenancyType]}</span>
                          ) : (
                            <span className="text-amber-700 dark:text-amber-400">No membership set</span>
                          )}
                          {m.hasUpcoming && (
                            <span className="ml-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-brand-700 dark:text-brand-300">
                              <Clock className="h-3 w-3" /> Upcoming
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="hidden px-5 py-3 md:table-cell">
                        {m.tenure ? (
                          <span className="text-foreground">{TENANCY_LABEL[m.tenure.tenancyType]}</span>
                        ) : (
                          <span className="whitespace-nowrap text-xs text-amber-700 dark:text-amber-400">No membership set</span>
                        )}
                        {m.hasUpcoming && (
                          <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
                            <Clock className="h-3 w-3" /> Upcoming
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 sm:px-5">{m.tenure ? <LifecycleBadge value={m.tenure.lifecycle} /> : '—'}</td>
                      <td className="hidden whitespace-nowrap px-5 py-3 text-muted-foreground md:table-cell">{m.tenure ? fmtDate(m.tenure.effectiveFrom) : '—'}</td>
                      <td className="px-3 py-3 text-right sm:px-5"><CaretRight className="ml-auto h-4 w-4 text-muted-foreground" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {selected && <MemberDrawer userId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function MemberDrawer({ userId, onClose }: { userId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [wizard, setWizard] = useState<TransitionAction | null>(null);

  const memberQ = useQuery({ queryKey: ['member', userId], queryFn: () => fetchMember(userId) });
  const m = memberQ.data;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['member', userId] });
    qc.invalidateQueries({ queryKey: ['members'] });
  };

  const current = m?.current ?? null;
  const isActive = current?.lifecycle === 'active';
  const hasCurrent = !!current;
  const hasUpcoming = (m?.upcoming?.length ?? 0) > 0;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <ScrollLock />
      <div
        className="h-full w-full max-w-md overflow-y-auto border-l border-border bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="flex items-center gap-2">
            <IdentificationCard className="h-4 w-4 text-brand-600 dark:text-brand-400" />
            <span className="text-sm font-semibold text-foreground">Membership</span>
          </div>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {memberQ.isLoading || !m ? (
          <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="space-y-5 p-5">
            <div>
              <div className="text-base font-semibold text-foreground">{m.user.firstName} {m.user.lastName}</div>
              <div className="text-xs text-muted-foreground">{m.user.email}</div>
            </div>

            {/* Current membership */}
            <div className="rounded-lg border border-border p-4">
              {current ? (
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-lg font-semibold text-foreground">{TENANCY_LABEL[current.tenancyType]}</div>
                    <div className="mt-1 text-xs text-muted-foreground">Since {fmtDate(current.effectiveFrom)}</div>
                  </div>
                  <LifecycleBadge value={current.lifecycle} />
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No membership recorded yet. Set one to begin their tenure.
                </div>
              )}
            </div>

            {/* Upcoming scheduled change */}
            {hasUpcoming && m.upcoming[0] && (
              <div className="rounded-lg border border-brand-300 bg-brand-50 p-3 dark:border-brand-500/40 dark:bg-brand-500/10">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-brand-900 dark:text-brand-200">
                    <Clock className="mr-1 inline h-3.5 w-3.5" />
                    Becomes <strong>{TENANCY_LABEL[m.upcoming[0].tenancyType]}</strong> on {fmtDate(m.upcoming[0].effectiveFrom)}
                  </div>
                  <button
                    onClick={() => setWizard('cancel_upcoming')}
                    className="text-xs font-medium text-brand-700 hover:underline dark:text-brand-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Actions */}
            <div>
              <div className="mb-2 text-xs font-medium text-muted-foreground">Actions</div>
              <div className="flex flex-wrap gap-2">
                {!hasCurrent && (
                  <Button variant="primary" size="sm" onClick={() => setWizard('provision')}>Set membership</Button>
                )}
                {isActive && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => setWizard('change_type')}>Change membership</Button>
                    <Button variant="outline" size="sm" onClick={() => setWizard('leave')}>Leave</Button>
                    <Button variant="outline" size="sm" onClick={() => setWizard('retire')}>Retire</Button>
                  </>
                )}
                {hasCurrent && !isActive && (
                  <Button variant="primary" size="sm" onClick={() => setWizard('reinstate')}>Reinstate</Button>
                )}
              </div>
            </div>

            {/* History timeline */}
            <div>
              <div className="mb-2 text-xs font-medium text-muted-foreground">History</div>
              {m.history.length === 0 ? (
                <div className="text-sm text-muted-foreground">No history yet.</div>
              ) : (
                <ol className="space-y-3">
                  {m.history.map((h) => (
                    <li key={h.id} className="relative border-l-2 border-border pl-4">
                      <div className="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-brand-500" />
                      <div className="flex items-center gap-2 text-sm text-foreground">
                        {TENANCY_LABEL[h.tenancyType]}
                        {h.lifecycle !== 'active' && <LifecycleBadge value={h.lifecycle} />}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {fmtDate(h.effectiveFrom)} → {h.effectiveTo ? fmtDate(h.effectiveTo) : 'present'}
                        {h.recordedBy && ` · by ${h.recordedBy.firstName} ${h.recordedBy.lastName}`}
                      </div>
                      {h.reason && <div className="mt-0.5 text-xs italic text-muted-foreground">“{h.reason}”</div>}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        )}
      </div>

      {wizard && m && (
        <ActionWizard
          action={wizard}
          member={m}
          onClose={() => setWizard(null)}
          onDone={() => {
            setWizard(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

const WIZARD_META: Record<TransitionAction, { title: string; verb: string }> = {
  provision: { title: 'Set membership', verb: 'Set' },
  change_type: { title: 'Change membership', verb: 'Change' },
  leave: { title: 'Record leaving chambers', verb: 'Confirm' },
  retire: { title: 'Record retirement', verb: 'Confirm' },
  reinstate: { title: 'Reinstate member', verb: 'Reinstate' },
  cancel_upcoming: { title: 'Cancel scheduled change', verb: 'Cancel change' },
};

function ActionWizard({
  action,
  member,
  onClose,
  onDone,
}: {
  action: TransitionAction;
  member: MemberDetail;
  onClose: () => void;
  onDone: () => void;
}) {
  const meta = WIZARD_META[action];
  const needsType = action === 'provision' || action === 'change_type' || action === 'reinstate';
  const allowsSchedule = action === 'change_type' || action === 'leave' || action === 'retire';

  const [tenancyType, setTenancyType] = useState<TenancyType>(
    member.current?.tenancyType ?? 'full_member',
  );
  const [reason, setReason] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await transitionMember(member.user.id, {
        action,
        ...(needsType && { tenancyType }),
        ...(reason.trim() && { reason: reason.trim() }),
        ...(allowsSchedule && effectiveFrom && { effectiveFrom: new Date(effectiveFrom).toISOString() }),
      });
      toast.success('Membership updated');
      onDone();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not update membership'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/50 p-4" onClick={onClose}>
      <ScrollLock />
      <div className="my-auto w-full max-w-sm rounded-lg border border-border bg-card shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border px-5 py-3 text-sm font-semibold text-foreground">{meta.title}</div>
        <div className="space-y-4 px-5 py-4">
          <p className="text-sm text-muted-foreground">
            {member.user.firstName} {member.user.lastName}
          </p>

          {action === 'cancel_upcoming' ? (
            <p className="text-sm text-foreground">Remove the scheduled change and keep the current membership?</p>
          ) : (
            <>
              {needsType && (
                <label className="block">
                  <span className="mb-1.5 block text-xs font-medium text-muted-foreground">Tenancy type</span>
                  <select
                    value={tenancyType}
                    onChange={(e) => setTenancyType(e.target.value as TenancyType)}
                    className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400"
                  >
                    {TENANCY_OPTIONS.map((t) => <option key={t} value={t}>{TENANCY_LABEL[t]}</option>)}
                  </select>
                </label>
              )}
              {allowsSchedule && (
                <label className="block">
                  <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
                    Effective date <span className="text-muted-foreground/60">(leave blank = now; a future date schedules it)</span>
                  </span>
                  <input
                    type="date"
                    value={effectiveFrom}
                    onChange={(e) => setEffectiveFrom(e.target.value)}
                    className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400"
                  />
                </label>
              )}
              <label className="block">
                <span className="mb-1.5 block text-xs font-medium text-muted-foreground">Reason (optional)</span>
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. promoted to full member"
                  className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
              </label>
            </>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
              <Warning className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : meta.verb}
          </Button>
        </div>
      </div>
    </div>
  );
}
